import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, del } from '../../../api/client';

/** 后端 order-staff.service.toStaffRow() 的唯一下发形状（list / create / update 同一份） */
interface OrderStaffItem {
  id: string; // 支出订单 id
  orderNumber: string;
  staffId: string;
  role: string | null;
  amount: number;
  /** 订单的「特别说明」整串（服务端拼接：人员调用 | 来源订单 | 分工 | 备注），不是备注原文 */
  notes: string | null;
  fullName: string | null;
  approvalStatus: string;
  paymentStatus: string;
  createdAt: string;
}

interface CustomerOption {
  id: string;
  fullName: string;
  phone: string | null;
}

/** /customers 接口单行原始数据（仅用到的字段） */
interface CustomerRow {
  id: string;
  /** Party.full_name 是 NOT NULL 的身份锚点，恒有值 */
  fullName: string;
  phone?: string | null;
}

interface Props {
  orderId: string;
  /**
   * 能否维护这张单的班组（新增与移除同一个判据）：由父组件传后端投影的 `canEditOrder`
   * （建单方 + 档期在途）。
   * 这里过去自备一份 `['未执行','执行中']` 白名单，服务端只挡「已取消」，
   * 于是绕开界面直接 POST 就能给一张已完成的单塞出新的支出订单——权威现在在服务端。
   */
  canAdd: boolean;
  /**
   * 本单服务对象（客户方）的 Party id，从候选里排除。
   * 候选池是「我的客户列表」，本单客户当然在里面——但他这张单是**被服务的一方**，
   * 把他选成调用人员等于给自己开一张指向自己的支出订单。服务端同一判据已收口
   * （`order-staff.service.create` 回 400），这里只是不再给一个点了必失败的选项。
   */
  orderCustomerId: string | null;
  /** 添加/删除成功后触发，供父组件刷新订单详情（含 totalRelatedExpense） */
  onChanged?: () => void;
}

/**
 * 默认常用分工，支持自由输入其它值（datalist 模式）
 */
const DEFAULT_ROLES = ['摄影', '摄像', '助理', '化妆', '司机', '讲师'];

/**
 * 长度上限：唯一事实源是后端 StaffItemDto（`order-staff.dto.ts`）。
 * 前端无法 import 后端模块，这里逐字对齐；改后端列宽时必须同时改这里，
 * 否则表现是「表单能提交，但要等后端返回 400 才知道超长」。
 */
const STAFF_ROLE_MAX_LENGTH = 50; // order.source_staff_role 与 service_type 都是 VarChar(50)
const STAFF_NOTES_MAX_LENGTH = 200;

/** 单条表单行的内部状态 */
interface StaffRow {
  rowId: string;
  staffId: string;
  staffKeyword: string;
  showPicker: boolean;
  role: string;
  amount: string;
  notes: string;
}

function newRow(): StaffRow {
  return {
    rowId: Math.random().toString(36).slice(2),
    staffId: '',
    staffKeyword: '',
    showPicker: false,
    role: '',
    amount: '',
    notes: '',
  };
}

function fmtCurrency(v: number | null | undefined): string {
  const n = Number(v ?? 0);
  if (!isFinite(n)) return '¥ 0.00';
  return `¥ ${n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function OrderStaffTable({ orderId, canAdd, orderCustomerId, onChanged }: Props) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [rows, setRows] = useState<StaffRow[]>([newRow()]);
  const [formError, setFormError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // 列表查询：get 已解过 { success, message, data } 外层，拿到的就是数组
  const { data: staffList = [], isLoading } = useQuery<OrderStaffItem[]>({
    queryKey: ['orderStaff', orderId],
    queryFn: () => get<OrderStaffItem[]>('/order-staff', { params: { orderId } }),
  });

  // 可选人员（选填时才拉，首次展开后复用）
  const { data: customers = [], isLoading: loadingCustomers } = useQuery<CustomerOption[]>({
    // 键必须带上排除对象：换一张单打开抽屉时，被排除的客户不一样，共用一份缓存会把上一单的候选池端过来
    queryKey: ['orderStaffCustomers', orderCustomerId],
    queryFn: async () => {
      const list = await get<CustomerRow[]>('/customers', { params: { pageSize: 500 } });
      return (list ?? [])
        .filter((c) => c.id !== orderCustomerId)
        .map(c => ({
          id: c.id,
          fullName: c.fullName,
          phone: c.phone ?? null,
        }));
    },
    enabled: showForm,
    staleTime: 60_000,
  });

  // 点击外部关闭所有行的下拉
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setRows(prev => prev.map(r => ({ ...r, showPicker: false })));
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // 批量创建
  const createMutation = useMutation({
    mutationFn: async (payload: { staffs: Array<{ staffId: string; role: string; amount: number; notes?: string }> }) => {
      return post('/order-staff', {
        orderId,
        staffs: payload.staffs,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderStaff', orderId] });
      resetForm();
      setShowForm(false);
      onChanged?.();
    },
    onError: (err: unknown) => {
      setFormError(err instanceof Error ? err.message : '添加失败，请重试');
    },
  });

  // 删除
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => del(`/order-staff/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderStaff', orderId] });
      onChanged?.();
    },
    onSettled: () => setDeletingId(null),
  });

  function resetForm() {
    setRows([newRow()]);
    setFormError('');
  }

  function updateRow(rowId: string, patch: Partial<StaffRow>) {
    setRows(prev => prev.map(r => (r.rowId === rowId ? { ...r, ...patch } : r)));
  }

  function removeRow(rowId: string) {
    setRows(prev => (prev.length <= 1 ? prev : prev.filter(r => r.rowId !== rowId)));
  }

  function addRow() {
    setRows(prev => [...prev, newRow()]);
  }

  function handleSubmit() {
    setFormError('');
    // 逐行校验
    const staffs: Array<{ staffId: string; role: string; amount: number; notes?: string }> = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.staffId) {
        setFormError(`第 ${i + 1} 行：请选择调用人员`);
        return;
      }
      if (!r.role.trim()) {
        setFormError(`第 ${i + 1} 行：请填写分工`);
        return;
      }
      const amt = Number(r.amount);
      // 必须先判空：Number('') === 0，只判 isFinite 的话空着金额会静默按 ¥0 提交。
      // 后端只保证「是数字、非负、不超精度」，必填这条产品语义由这里守
      if (r.amount.trim() === '' || !isFinite(amt) || amt < 0) {
        setFormError(`第 ${i + 1} 行：请填写有效的费用金额`);
        return;
      }
      // 长度上限与后端 StaffItemDto 同源（分工落在 VarChar(50)），提前拦住省一个来回
      if (r.role.trim().length > STAFF_ROLE_MAX_LENGTH) {
        setFormError(`第 ${i + 1} 行：分工长度不能超过 ${STAFF_ROLE_MAX_LENGTH} 个字符`);
        return;
      }
      if (r.notes.trim().length > STAFF_NOTES_MAX_LENGTH) {
        setFormError(`第 ${i + 1} 行：备注长度不能超过 ${STAFF_NOTES_MAX_LENGTH} 个字符`);
        return;
      }
      staffs.push({
        staffId: r.staffId,
        role: r.role.trim(),
        amount: amt,
        notes: r.notes.trim() || undefined,
      });
    }
    createMutation.mutate({ staffs });
  }

  function handleDelete(id: string) {
    if (deletingId) return;
    if (!window.confirm('确定要删除此条人员调用吗？对应的支出订单将被变更为「已取消」，订单数据仍会保留在系统中。')) return;
    setDeletingId(id);
    deleteMutation.mutate(id);
  }

  const totalAmount = staffList.reduce((sum, it) => sum + it.amount, 0);

  return (
    <div ref={rootRef}>
      {/* 标题 + 添加按钮 */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
          人员调用表
          {staffList.length > 0 && (
            <span className="text-[10px] bg-gray-200 text-gray-500 rounded-full px-1.5 py-0.5 font-medium">
              {staffList.length}
            </span>
          )}
          <span className="text-[10px] text-gray-400 font-normal normal-case tracking-normal ml-1">
            · 支出合计 {fmtCurrency(totalAmount)}
          </span>
        </p>
        {!showForm && canAdd && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1 text-xs text-teal-500 hover:text-teal-600 cursor-pointer transition-colors whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-user-add-line text-sm" />
            </div>
            添加人员
          </button>
        )}
      </div>

      {/* 批量添加表单 */}
      {showForm && (
        <div className="mb-4 bg-teal-50 border border-teal-200 rounded-xl p-3 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-teal-700 font-medium flex items-center gap-1">
              <i className="ri-information-line text-sm" />
              每行将生成一条独立的支出订单
            </p>
            <button
              type="button"
              onClick={addRow}
              className="text-xs text-teal-600 hover:text-teal-700 cursor-pointer flex items-center gap-1 whitespace-nowrap"
            >
              <i className="ri-add-line text-sm" />
              增加一行
            </button>
          </div>

          {rows.map((row, idx) => {
            const selectedStaff = customers.find(c => c.id === row.staffId) ?? null;
            const kw = row.staffKeyword.trim().toLowerCase();
            const filtered = !kw
              ? customers.slice(0, 30)
              : customers.filter(c =>
                  (c.fullName ?? '').toLowerCase().includes(kw) ||
                  (c.phone ?? '').toLowerCase().includes(kw),
                ).slice(0, 30);

            return (
              <div key={row.rowId} className="bg-white rounded-lg border border-teal-100 p-3 space-y-2 relative">
                {/* 行序号 + 删除按钮 */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-teal-600 font-semibold">第 {idx + 1} 人</span>
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRow(row.rowId)}
                      className="text-xs text-gray-400 hover:text-rose-400 cursor-pointer flex items-center gap-0.5"
                    >
                      <i className="ri-close-line text-sm" />
                      移除
                    </button>
                  )}
                </div>

                {/* 选择人员 */}
                <div className="relative">
                  <label className="block text-xs text-gray-500 mb-1">调用人员 <span className="text-rose-400">*</span></label>
                  {selectedStaff ? (
                    <div className="flex items-center justify-between px-3 py-2 bg-white border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded-full bg-teal-500 text-white text-xs flex items-center justify-center shrink-0">
                          {(selectedStaff.fullName || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-gray-800 truncate">{selectedStaff.fullName || '未命名'}</p>
                          {selectedStaff.phone && <p className="text-xs text-gray-400 truncate">{selectedStaff.phone}</p>}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => updateRow(row.rowId, { staffId: '', showPicker: true })}
                        className="text-xs text-gray-400 hover:text-teal-500 cursor-pointer whitespace-nowrap"
                      >
                        更换
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={row.staffKeyword}
                        onChange={e => updateRow(row.rowId, { staffKeyword: e.target.value, showPicker: true })}
                        onFocus={() => updateRow(row.rowId, { showPicker: true })}
                        placeholder="输入姓名或电话搜索..."
                        className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all placeholder:text-gray-400"
                      />
                      {row.showPicker && (
                        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {loadingCustomers ? (
                            <div className="p-3 text-center text-xs text-gray-400">
                              <i className="ri-loader-4-line animate-spin" /> 加载中...
                            </div>
                          ) : filtered.length === 0 ? (
                            <div className="p-3 text-center text-xs text-gray-400">无匹配人员</div>
                          ) : (
                            filtered.map(c => (
                              <button
                                type="button"
                                key={c.id}
                                onClick={() => updateRow(row.rowId, {
                                  staffId: c.id,
                                  staffKeyword: '',
                                  showPicker: false,
                                })}
                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-teal-50 cursor-pointer text-left transition-colors"
                              >
                                <div className="w-6 h-6 rounded-full bg-gray-200 text-gray-600 text-xs flex items-center justify-center shrink-0">
                                  {(c.fullName || '?').charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm text-gray-700 truncate">{c.fullName || '未命名'}</p>
                                  {c.phone && <p className="text-xs text-gray-400 truncate">{c.phone}</p>}
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* 分工 + 费用 */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">分工 <span className="text-rose-400">*</span></label>
                    <input
                      type="text"
                      list="order-staff-role-options"
                      maxLength={STAFF_ROLE_MAX_LENGTH}
                      value={row.role}
                      onChange={e => updateRow(row.rowId, { role: e.target.value })}
                      placeholder="如：摄影 / 助理"
                      className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all placeholder:text-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">费用金额 <span className="text-rose-400">*</span></label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">¥</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.amount}
                        onChange={e => updateRow(row.rowId, { amount: e.target.value })}
                        placeholder="0.00"
                        className="w-full pl-7 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all placeholder:text-gray-400"
                      />
                    </div>
                  </div>
                </div>

                {/* 备注 */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">备注（选填）</label>
                  <textarea
                    value={row.notes}
                    onChange={e => updateRow(row.rowId, { notes: e.target.value })}
                    rows={2}
                    maxLength={STAFF_NOTES_MAX_LENGTH}
                    placeholder="补充说明..."
                    className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 resize-none transition-all placeholder:text-gray-400"
                  />
                </div>
              </div>
            );
          })}

          <datalist id="order-staff-role-options">
            {DEFAULT_ROLES.map(r => <option key={r} value={r} />)}
          </datalist>

          {formError && (
            <p className="text-xs text-rose-500 flex items-center gap-1">
              <i className="ri-error-warning-line text-xs" />
              {formError}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => { resetForm(); setShowForm(false); }}
              disabled={createMutation.isPending}
              className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors bg-white"
            >
              取消
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={createMutation.isPending}
              className="px-4 py-1.5 text-xs bg-teal-500 text-white rounded-lg hover:bg-teal-600 cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors flex items-center gap-1"
            >
              {createMutation.isPending
                ? <i className="ri-loader-4-line animate-spin text-xs" />
                : <i className="ri-check-line text-xs" />}
              保存（生成 {rows.length} 条支出订单）
            </button>
          </div>
        </div>
      )}

      {/* 列表 */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map(i => (
            <div key={i} className="h-10 bg-gray-50 animate-pulse rounded-lg" />
          ))}
        </div>
      ) : staffList.length === 0 ? (
        <div className="flex flex-col items-center py-6 text-center">
          <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full mb-2">
            <i className="ri-team-line text-gray-300 text-lg" />
          </div>
          <p className="text-xs text-gray-400">
            {canAdd ? '暂无人员调用记录，点击「添加人员」录入' : '暂无人员调用记录'}
          </p>
        </div>
      ) : (
        // 简约版行列表：参照「人员调用核算」中的补生支出订单行样式，不显示电话；hover 显示删除按钮（隐性删除）
        <div className="space-y-1.5">
          {staffList.map(item => (
            <div
              key={item.id}
              className="group relative flex items-center justify-between gap-2 px-2.5 py-2 bg-white hover:bg-gray-50/80 border border-gray-100 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-[10px] font-mono text-gray-400 truncate">{item.orderNumber}</span>
                <span className="text-xs text-gray-700 truncate">{item.fullName ?? '-'}</span>
                {item.role && <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full shrink-0">{item.role}</span>}
                {item.approvalStatus && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${item.approvalStatus === '待审核' ? 'bg-amber-50 text-amber-600' : item.approvalStatus === '已通过' ? 'bg-teal-50 text-teal-600' : 'bg-rose-50 text-rose-500'}`}>{item.approvalStatus}</span>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-xs font-semibold text-rose-500">¥{item.amount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
                {canAdd && (
                  <button
                    onClick={() => handleDelete(item.id)}
                    disabled={deletingId === item.id}
                    className="opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center text-gray-300 hover:text-rose-400 cursor-pointer transition-all"
                    title="删除此条人员调用（订单会变更为已取消）"
                  >
                    {deletingId === item.id
                      ? <i className="ri-loader-4-line animate-spin text-xs" />
                      : <i className="ri-close-line text-sm" />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 备注行（展示 notes 字段）*/}
      {staffList.some(it => it.notes) && (
        <div className="mt-2 space-y-1">
          {staffList
            .filter(it => it.notes)
            .map(it => (
              <p key={`note-${it.id}`} className="text-xs text-gray-400 pl-2 border-l-2 border-gray-100">
                <span className="text-gray-500">{it.fullName}：</span>{it.notes}
              </p>
            ))}
        </div>
      )}
    </div>
  );
}
